import { useState, useEffect, useCallback, useRef } from 'react';

interface UseTimerReturn {
  timerValue: number;
  startTimer: (timeInSeconds: number) => void;
  stopTimer: () => void;
  isTimerRunning: boolean;
}

const useTimer = (): UseTimerReturn => {
  const [timerValue, setTimerValue] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);
  const intervalRef = useRef<number | null>(null);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTimerRunning(false);
  }, []);

  const startTimer = useCallback(
    (timeInSeconds: number) => {
      stopTimer();
      setTimerValue(timeInSeconds);
      setIsTimerRunning(true);

      intervalRef.current = window.setInterval(() => {
        setTimerValue(prevTime => {
          if (prevTime <= 1) {
            stopTimer();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    },
    [stopTimer]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    timerValue,
    startTimer,
    stopTimer,
    isTimerRunning,
  };
};

export { useTimer };
